import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted } from 'vue';

import type { Consent } from '@/stores/user.store';

import { initTrackers } from '@/main.common';
import { CollectionType } from '@/enums/collection-type';

import { logTalentAd } from '@/helpers/ad/talent-ad';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { isSameWeek } from '@/helpers/date-helper';
import { updateUserProfile } from '@/helpers/id-helper';
import { initSentry } from '@/helpers/sentry-helper';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { isMobile } from '@/helpers/ui-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import TitleRemovalBanner from '@/components/banners/TitleRemovalBanner.vue';
import AdBlockerBait from './AdBlockerBait.vue';
import { lowPriorityCallback } from '@/helpers/request-idle-helper';
import {
	useAppStore,
	useLanguageStore,
	useRoutingStore,
	useTrackingStore,
	useUserStore,
} from '@/helpers/composables/useStores';
import { getVm } from '@/helpers/vm-helper';
import { useVerifyEmail } from '@/helpers/composables/useVerifyEmail';


export default /*#__PURE__*/_defineComponent({
  __name: 'LowPriorityComponents',
  setup(__props) {

const ConsentBanner = () => import(/* webpackChunkName: "consent-banner" */ '@/components/consent/ConsentBanner.vue');
const ListsForSignedInUsersOnboarding = () =>
	import(/* webpackChunkName: "consent-banner" */ '@/components/modals/ListsForSignedInUsersOnboarding.vue');
const ConsentOverlay = () =>
	import(/* webpackChunkName: "consent-overlay" */ '@/components/consent/ConsentOverlay.vue');
const FilterMenu = () => import(/* webpackChunkName: "filter-menu" */ '@/components/filter-bar/FilterMenu.vue');
const MobileBanner = () => import(/* webpackChunkName: "mobile-banner" */ '@/components/MobileBanner.vue');
const VerifyEmailBanner = () =>
	import(/* webpackChunkName: "verify-email-banner" */ '@/components/modals/account/VerifyEmailBanner.vue');

const vm = getVm();

const { country, language, webLocale } = useLanguageStore();
const { lastActiveCollectionType, activeCollectionType, activeRoute } = useRoutingStore();
const { saveSettings, updateUserVisitStatus, isLoggedIn, jwId, lastTimeMobileAppBannerWasClosedTS } = useUserStore();
const { isArbitrating, arbitration } = useArbitrage();
const { setSessionEnvironment } = useTrackingStore();
const { xServeTrack, isSeoUser } = useAppStore();
const { showBanner, userCreationTime } = useVerifyEmail();

const consentComponent = computed(() => {
	if (!global.VUE_APP_TESTS_E2E && hasUCConsentBanner(webLocale.value)) {
		return ConsentOverlay;
	}

	return ConsentBanner;
});
const showMobileAppBanner = computed(() => {
	return (
		!isLoggedIn.value &&
		!isSameWeek(new Date(lastTimeMobileAppBannerWasClosedTS.value), new Date()) &&
		!isArbitrating.value
	);
});

const shouldRenderMenu = computed(() => {
	return (
		(!!activeCollectionType.value &&
			[
				CollectionType.NEW,
				CollectionType.POPULAR,
				CollectionType.SEARCH,
				CollectionType.MY_LISTS,
				CollectionType.TV_SHOW_TRACKING,
				CollectionType.PUBLIC_LISTS,
				CollectionType.TITLELIST,
				CollectionType.UPCOMING,
			].includes(activeCollectionType.value)) ||
		// editorial page is being rendered after Filter Menu tries to attach itself to Content base id.
		// Would perhaps be better to have all cases work this way rather than with CollectionType, which can be true beforeEnter()
		(activeRoute.value?.name || '').includes('app.editorial')
	);
});

onMounted(() => {
	if (process.env.NODE_ENV !== 'development') {
		logTalentAd();
	}

	setSnowplowSessionEnvironmentContext();
	lowPriorityCallback(() => initTrackers(vm.$store));
	lowPriorityCallback(() => initSentry());
	updateUserVisitStatus();

	if (isMobile()) {
		// @ts-ignore
		import('long-press-event/dist/long-press-event.min.js');
	}

	// NOTE This is only for testing (otherwise we have no way to force old consents)
	(window as any).saveSettings = async (consents: Record<Consent, boolean>) => {
		await saveSettings({ jw_consents: consents });
	};

	if (country.value) {
		try {
			updateUserProfile(jwId.value, country.value, language.value);
		} catch (error: any) {
			TrackingHelper.trackEvent('app_error', {
				action: 'update_user_profile_failure',
				label: error,
			});
		}
	}
});

function setSnowplowSessionEnvironmentContext() {
	if (process.server) {
		return;
	}
	const referrer = document ? document.referrer : null;
	const url = window ? window.location.href : null;

	const params = getQueryParams(url);
	const origin = getOrigin(referrer);

	setSessionEnvironment({
		origin,
		originPageUrl: url,
		originPageReferred: referrer,
		originRefrMedium: arbitration.value?.utm_medium || params.medium,
		originRefrSource: arbitration.value?.utm_source || params.source,
		originRefrSocial: origin === 'social' ? getSocialPlatform(referrer) : null,
		env: xServeTrack.value,
	});
}

function getQueryParams(url: string | null) {
	if (!url) {
		return { medium: null, source: null };
	}
	const params = new URL(url).searchParams;
	return {
		medium: params.get('utm_medium') || null,
		source: params.get('utm_source') || null,
	};
}

function getOrigin(referrer: string | null) {
	if (isArbitrating.value) {
		return 'search';
	} else if (isSeoUser.value) {
		return 'seo';
	} else if (isSocial(referrer)) {
		return 'social';
	} else if (!referrer) {
		return 'direct';
	}

	// no paid search, seoUser false, no social, has refferer - assume returning
	return 'returning';
}

function isSocial(referrer: string | null) {
	if (!referrer) return false;

	const socialRegex =
		/(?:https?:\/\/)?(?:\w+\.)?(t\.co|facebook\.com|x\.com|twitter\.com|instagram\.com|youtube\.com|tiktok\.com|snapchat\.com|reddit\.com|bsky\.app|linkedin\.com)/i;
	return socialRegex.test(referrer);
}

function getSocialPlatform(referrer: string | null): string | null {
	if (!referrer) return null;

	const socialRegex =
		/(?:https?:\/\/)?(?:\w+\.)?(t\.co|facebook\.com|x\.com|twitter\.com|instagram\.com|youtube\.com|tiktok\.com|snapchat\.com|reddit\.com|bsky\.app|linkedin\.com)/i;

	const match = referrer.match(socialRegex);
	return match ? match[1].replace(/\.com$/, '') : null;
}

return { __sfc: true,ConsentBanner, ListsForSignedInUsersOnboarding, ConsentOverlay, FilterMenu, MobileBanner, VerifyEmailBanner, vm, country, language, webLocale, lastActiveCollectionType, activeCollectionType, activeRoute, saveSettings, updateUserVisitStatus, isLoggedIn, jwId, lastTimeMobileAppBannerWasClosedTS, isArbitrating, arbitration, setSessionEnvironment, xServeTrack, isSeoUser, showBanner, userCreationTime, consentComponent, showMobileAppBanner, shouldRenderMenu, setSnowplowSessionEnvironmentContext, getQueryParams, getOrigin, isSocial, getSocialPlatform, TitleRemovalBanner, AdBlockerBait }
}

})